import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Sheet, IndianRupee, TrendingUp, Sparkles, BarChart3 } from 'lucide-react';

export default function Finance({ currentUser }) {
  const [financeApplications, setFinanceApplications] = useState([]);
  const [showSheetsDialog, setShowSheetsDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [sheetsConfig, setSheetsConfig] = useState({
    spreadsheet_id: '',
    sheet_name: 'Finance',
    start_row: 2
  });
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchFinanceApplications();
  }, []);

  const fetchFinanceApplications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance`, {
        credentials: 'include'
      });
      const data = await response.json();
      setFinanceApplications(data);
    } catch (error) {
      console.error('Error fetching finance applications:', error);
    }
  };

  const handleGoogleSheetsImport = async () => {
    if (!sheetsConfig.spreadsheet_id) {
      alert('Please enter Spreadsheet ID or URL');
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance/import-sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sheetsConfig)
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Import successful! ✅\n\nImported: ${data.imported}\nSkipped: ${data.skipped}\nTotal: ${data.total_rows}`);
        setShowSheetsDialog(false);
        fetchFinanceApplications();
      } else {
        const error = await response.json();
        alert(`Import failed ❌\n\n${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error importing:', error);
      alert('Error importing from Google Sheets');
    } finally {
      setImporting(false);
    }
  };

  const handleAIAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance/ai-analysis`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAiAnalysis(data);
        setShowAIDialog(true);
      } else {
        alert('AI analysis failed');
      }
    } catch (error) {
      console.error('Error analyzing:', error);
      alert('Error running AI analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const getProviderColor = (provider) => {
    const colors = {
      'ratnafin': 'bg-green-100 text-green-800',
      'gov_bank': 'bg-blue-100 text-blue-800',
      'bajaj': 'bg-orange-100 text-orange-800',
      'solfin': 'bg-purple-100 text-purple-800',
      'ecofy': 'bg-pink-100 text-pink-800'
    };
    return colors[provider] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'disbursed': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate stats
  const totalAmount = financeApplications.reduce((sum, app) => sum + (app.loan_amount || 0), 0);
  const approvedCount = financeApplications.filter(app => app.status === 'approved').length;
  const pendingCount = financeApplications.filter(app => app.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Finance & Loans</h1>
          <p className="text-muted-foreground mt-1">
            Track loan applications, disbursements, and finance partners
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAIAnalysis} disabled={analyzing}>
            <Sparkles className="mr-2 h-4 w-4" />
            {analyzing ? 'Analyzing...' : 'AI Analysis'}
          </Button>
          <Dialog open={showSheetsDialog} onOpenChange={setShowSheetsDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!!currentUser?.branch}>
                <Sheet className="mr-2 h-4 w-4" />
                Import from Sheets
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Import Finance Data</DialogTitle>
                <DialogDescription>
                  Import loan applications from your spreadsheet
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-md text-sm">
                  <p className="font-semibold mb-2">🔑 Need API Key (free, 2 min):</p>
                  <p className="text-xs"><a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener" className="text-blue-600 underline">Get free key here</a> → Paste below</p>
                </div>
                
                <div>
                  <Label htmlFor="api_key">Google Sheets API Key *</Label>
                  <Input
                    id="api_key"
                    type="password"
                    value={sheetsConfig.api_key}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, api_key: e.target.value})}
                    placeholder="AIzaSy..."
                  />
                </div>
                
                <div>
                  <Label htmlFor="spreadsheet_id">Spreadsheet ID or URL *</Label>
                  <Input
                    id="spreadsheet_id"
                    value={sheetsConfig.spreadsheet_id}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, spreadsheet_id: e.target.value})}
                    placeholder="Paste URL or ID"
                  />
                </div>
                <div>
                  <Label htmlFor="sheet_name">Sheet Name</Label>
                  <Input
                    id="sheet_name"
                    value={sheetsConfig.sheet_name}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, sheet_name: e.target.value})}
                    placeholder="Finance"
                  />
                </div>
                <div>
                  <Label htmlFor="start_row">Start Row</Label>
                  <Input
                    id="start_row"
                    type="number"
                    value={sheetsConfig.start_row}
                    onChange={(e) => setSheetsConfig({...sheetsConfig, start_row: parseInt(e.target.value) || 2})}
                  />
                </div>
                <div className="bg-green-50 border border-green-200 p-3 rounded-md text-sm">
                  <p className="font-semibold mb-1 text-green-800">✅ Quick Import</p>
                  <p className="text-xs text-green-700">Paste sheet URL and import your finance data!</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowSheetsDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleGoogleSheetsImport} disabled={importing}>
                    {importing ? 'Importing...' : 'Try Import'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{financeApplications.length}</div>
              <p className="text-sm text-muted-foreground">Total Applications</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{approvedCount}</div>
              <p className="text-sm text-muted-foreground">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">{pendingCount}</div>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold">₹{(totalAmount / 100000).toFixed(1)}L</div>
              <p className="text-sm text-muted-foreground">Total Loan Amount</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Finance Applications */}
      <div className="grid gap-4">
        {financeApplications.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <IndianRupee className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No finance applications. Import from Google Sheets or add manually.</p>
            </CardContent>
          </Card>
        ) : (
          financeApplications.map((app) => (
            <Card key={app.finance_id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{app.customer_name || app.project_id}</h3>
                      <Badge className={getProviderColor(app.provider)}>
                        {app.provider?.toUpperCase()}
                      </Badge>
                      <Badge className={getStatusColor(app.status)}>
                        {app.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm text-muted-foreground">
                      <div>
                        <span className="font-medium">Loan Amount:</span> ₹{(app.loan_amount || 0).toLocaleString()}
                      </div>
                      <div>
                        <span className="font-medium">Disbursement:</span> {app.disbursement_model || '70-30'}
                      </div>
                      <div>
                        <span className="font-medium">Branch:</span> {app.branch}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline">
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* AI Analysis Dialog */}
      {aiAnalysis && (
        <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                AI Finance Analysis
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Key Insights</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  {aiAnalysis.insights?.map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 mt-0.5 text-green-600" />
                      <p>{insight}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Recommendations</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  {aiAnalysis.recommendations?.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <BarChart3 className="h-4 w-4 mt-0.5 text-blue-600" />
                      <p>{rec}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
